import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { t } from '@/lib/i18n';
import { EmptyState, Spinner } from './layout';

export interface Column<T> {
  header: ReactNode;
  cell: (row: T) => ReactNode;
  className?: string;
}

export function DataTable<T extends { _id: string }>({
  columns,
  rows,
  loading,
  empty = t('Nothing here yet'),
  rowLink,
  onRowHover,
  highlight,
}: {
  columns: Column<T>[];
  rows?: T[];
  loading?: boolean;
  empty?: string;
  rowLink?: (row: T) => string;
  /** Called with the row under the pointer (null when it leaves the table). */
  onRowHover?: (row: T | null) => void;
  /** Id of a row to highlight (e.g. the one selected on the map). */
  highlight?: string | null;
}) {
  const navigate = useNavigate();
  // Columns are static per table, so their position is a stable key.
  const keyed = columns.map((c, i) => ({ ...c, key: `col-${i}` }));
  if (loading && !rows) return <Spinner />;
  if (!rows?.length) return <EmptyState title={empty} />;
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50">
          <tr>
            {keyed.map((c) => (
              <th
                key={c.key}
                className={`px-4 py-2.5 text-left text-xs font-semibold tracking-wide text-slate-500 uppercase ${c.className ?? ''}`}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody
          className={`divide-y divide-slate-100 ${loading ? 'opacity-60' : ''}`}
          onMouseLeave={onRowHover ? () => onRowHover(null) : undefined}
        >
          {rows.map((row) => (
            <tr
              key={row._id}
              onClick={rowLink ? () => navigate(rowLink(row)) : undefined}
              onMouseEnter={onRowHover ? () => onRowHover(row) : undefined}
              className={`${rowLink ? 'cursor-pointer hover:bg-slate-50' : ''} ${highlight === row._id ? 'bg-amber-50' : ''}`}
            >
              {keyed.map((c) => (
                <td key={c.key} className={`px-4 py-2.5 align-middle text-slate-700 ${c.className ?? ''}`}>
                  {c.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
